/**
 * Charts, drawn as SVG by hand.
 *
 * No charting library. The two candidates each weigh more than this whole
 * application, and everything here needs to do is draw bars, a line, and a
 * few axis labels. Hand-drawn also means the type, colour and spacing come
 * from the same tokens as the rest of the app rather than from a library's
 * defaults, which is most of what makes charts look bolted on.
 */

import { formatMoneyShort } from "../lib/money";

export interface Point {
  label: string;
  value: number;
  /** Shown instead of the raw number when the bar is read. */
  display?: string;
}

/* ------------------------------------------------------------------ */
/* Bars                                                                */
/* ------------------------------------------------------------------ */

/**
 * A column chart with the value written above each bar.
 *
 * Values are money in cents unless `money` is false. The axis is deliberately
 * absent: with the figure printed on every column, a scale down the side is
 * one more thing to read and nothing to learn from.
 */
export function BarChart({
  data, height = 172, money = true, tone = "sky",
}: {
  data: Point[];
  height?: number;
  money?: boolean;
  tone?: "sky" | "ok" | "amber";
}) {
  if (!data.length) return null;
  const max = Math.max(...data.map((d) => d.value), 1);

  /* Laid out with flex boxes rather than a stretched SVG. An SVG scaled with
     preserveAspectRatio="none" cannot carry text — the glyphs squash — so the
     figures had to sit outside it in a row of their own, floating away from
     the bars they described. Here each figure sits on top of its own bar. */
  return (
    <div className="chart" style={{ height }}>
      {data.map((d) => (
        <div className="chart-col" key={d.label}>
          <span className="chart-v">
            {d.display ?? (money ? formatMoneyShort(d.value) : String(d.value))}
          </span>
          <div className="chart-track">
            <div className={`chart-bar bar-${tone}`}
                 style={{ height: `${Math.max(d.value > 0 ? 3 : 0, (d.value / max) * 100)}%` }} />
          </div>
          <span className="chart-l">{d.label}</span>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* A line, small enough to sit in a row                                */
/* ------------------------------------------------------------------ */

export function Sparkline({ values, tone = "sky" }: {
  values: number[];
  tone?: "sky" | "ok" | "red";
}) {
  if (values.length < 2) return null;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = Math.max(1, max - min);
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * 100;
    const y = 26 - ((v - min) / span) * 24;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });
  return (
    <svg viewBox="0 0 100 28" preserveAspectRatio="none"
         className={`spark spark-${tone}`} aria-hidden="true">
      <polyline points={pts.join(" ")} fill="none" strokeWidth="2"
                vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Proportions of a whole                                              */
/* ------------------------------------------------------------------ */

/**
 * A single horizontal bar split into segments — how the lot divides by
 * status, how a month's gross splits by make. Reads faster than a pie and
 * takes a fraction of the room.
 */
export function SplitBar({ parts }: {
  parts: { label: string; value: number; tone: string }[];
}) {
  const total = parts.reduce((n, p) => n + p.value, 0);
  if (!total) return null;
  return (
    <div className="splitwrap">
      <div className="split">
        {parts.filter((p) => p.value > 0).map((p) => (
          <span key={p.label} className={`split-seg tone-${p.tone}`}
                style={{ width: `${(p.value / total) * 100}%` }}
                title={`${p.label}: ${p.value}`} />
        ))}
      </div>
      <div className="split-key">
        {parts.filter((p) => p.value > 0).map((p) => (
          <span key={p.label} className="split-item">
            <span className={`split-dot tone-${p.tone}`} />
            {p.label} <b>{p.value}</b>
          </span>
        ))}
      </div>
    </div>
  );
}
